import {
  Control,
  Controller,
  UseFormStateReturn,
  useWatch,
} from 'react-hook-form';
import { Subheader } from '../../../tailwindComponents/Subheader';
import { Input } from '../../../tailwindComponents/Input';
import { Select } from '../../../tailwindComponents/Select';
import { WarehouseFormValues } from '../types';
import {
  CLOSING_TIME_FIELD_NAMES,
  DAYS,
  OPENING_TIME_FIELD_NAMES,
  STANDARD_BUSINESS_HOURS,
  WORKING_FIELD_NAMES,
} from '../constants';
import { Timepicker } from '../../../tailwindComponents/Timepicker';
import { Checkbox } from '../../../tailwindComponents/Checkbox';

function DayRow({
  keyName,
  booleanKeyName,
  openingTimeKeyName,
  closingTimeKeyName,
  label,
  control,
  formState,
  operating = false,
}: {
  keyName: string;
  booleanKeyName: string;
  openingTimeKeyName: string;
  closingTimeKeyName: string;
  label: string;
  control: Control<WarehouseFormValues>;
  formState: UseFormStateReturn<WarehouseFormValues>;
  operating?: boolean;
}) {
  type WorkingFieldName = (typeof WORKING_FIELD_NAMES)[number];
  type OpeningTimeFieldName = (typeof OPENING_TIME_FIELD_NAMES)[number];
  type ClosingTimeFieldName = (typeof CLOSING_TIME_FIELD_NAMES)[number];

  const working = useWatch({
    control,
    name: `${booleanKeyName}` as WorkingFieldName,
  });

  return (
    <div key={keyName} className='flex items-center gap-x-6'>
      <Controller
        name={booleanKeyName as WorkingFieldName}
        control={control}
        render={({ field }) => (
          <div className='min-w-[12rem] h-8 flex items-center'>
            <Checkbox
              label={`${
                operating ? `Operating on ${label}` : `Working on ${label}`
              }`}
              checked={!!field.value}
              onChange={field.onChange}
              name={field.name}
              ref={field.ref}
            />
          </div>
        )}
      />
      <Controller
        name={openingTimeKeyName as OpeningTimeFieldName}
        control={control}
        render={({ field }) => (
          <Timepicker
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!(formState.errors as any)?.[keyName]?.openingTime}
            optional={!working}
            orientation='horizontal'
            disabled={!working}
          />
        )}
      />
      <span className='text-gray-400 text-lg select-none'>–</span>
      <Controller
        name={closingTimeKeyName as ClosingTimeFieldName}
        control={control}
        render={({ field }) => (
          <Timepicker
            name={field.name}
            value={field.value}
            onChange={field.onChange}
            isInvalid={!!(formState.errors as any)?.[keyName]?.closingTime}
            optional={!working}
            orientation='horizontal'
            disabled={!working}
          />
        )}
      />
    </div>
  );
}

export function Hours({
  control,
  formState,
}: {
  control: Control<WarehouseFormValues>;
  formState: UseFormStateReturn<WarehouseFormValues>;
}) {
  const schedule = useWatch({
    control,
    name: 'schedule',
  });

  return (
    <>
      <Subheader subHeader='Working Hours' />
      <div className='grid grid-cols-[minmax(0,14rem)_10rem_8rem_minmax(0,14rem)_10rem] items-center gap-x-4 gap-y-3'>
        {/* Row 1 */}
        <label className='text-sm font-medium text-gray-900'>Schedule</label>
        <Controller
          name='schedule'
          control={control}
          render={({ field }) => (
            <Select options={STANDARD_BUSINESS_HOURS} selected={field.value} setSelected={field.onChange} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Maximum headcount</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='maximumHeadcount'
          control={control}
          render={({ field }) => (
            <Input {...field} name='maximumHeadcount' isInvalid={!!formState.errors.maximumHeadcount} validationMessage={formState.errors.maximumHeadcount?.message} />
          )}
        />

        {/* Row 2 */}
        <div><label className='text-sm font-medium text-gray-900'>Shifts number</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='shifts'
          control={control}
          render={({ field }) => (
            <Input {...field} name='shifts' isInvalid={!!formState.errors.shifts} validationMessage={formState.errors.shifts?.message} />
          )}
        />
        <div />
        <div><label className='text-sm font-medium text-gray-900'>Minimum headcount</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='minimumHeadcount'
          control={control}
          render={({ field }) => (
            <Input {...field} name='minimumHeadcount' isInvalid={!!formState.errors.minimumHeadcount} validationMessage={formState.errors.minimumHeadcount?.message} />
          )}
        />

        {/* Row 3 */}
        <div><label className='text-sm font-medium text-gray-900'>Cut-off time for tomorrow&apos;s despatch planning</label><p className='text-sm text-gray-400'>Optional</p></div>
        <Controller
          name='cutOffTime'
          control={control}
          render={({ field }) => (
            <Timepicker name={field.name} value={field.value} onChange={field.onChange} />
          )}
        />
        <div />
        <div />
        <div />
      </div>
      {schedule.value === STANDARD_BUSINESS_HOURS[0].value ? (
        <Controller
          name='flexibleHoursOnDemand'
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Flexible hours on demand'
              checked={field.value}
              onChange={field.onChange}
              name={field.name}
              ref={field.ref}
            />
          )}
        />
      ) : (
        <div className='flex flex-col space-y-4'>
          {DAYS.map(({ key, label }) => (
            <DayRow
              key={key}
              keyName={`${key}WorkingHours`}
              booleanKeyName={`${key}WorkingHours.working`}
              openingTimeKeyName={`${key}WorkingHours.openingTime`}
              closingTimeKeyName={`${key}WorkingHours.closingTime`}
              label={label}
              control={control}
              formState={formState}
            />
          ))}
        </div>
      )}
      <Subheader subHeader='Loading Hours' />
      <div className='flex flex-col space-y-4'>
        {DAYS.map(({ key, label }) => (
          <DayRow
            key={key}
            keyName={`${key}LoadingHours`}
            booleanKeyName={`${key}LoadingHours.operating`}
            openingTimeKeyName={`${key}LoadingHours.openingTime`}
            closingTimeKeyName={`${key}LoadingHours.closingTime`}
            label={label}
            control={control}
            formState={formState}
            operating
          />
        ))}
      </div>
    </>
  );
}
